import React, { Fragment, useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import ReactRouterPropTypes from 'react-router-prop-types';
import PropTypes from 'prop-types';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import {
  startOfMonth,
  lastDayOfMonth,
  differenceInMonths,
  format,
} from 'date-fns';
import {
  changeLoadingTrue,
  changeLoadingFalse,
} from '../../redux/actions/loadingActions';
import MaterialTable from '../../shared/components/material-table';
import GraphQl from '../../GraphQL';
import { showNotificationAutoDestroy } from '../../shared/components/ShowNotification';
import {
  DEFAULT_PAGE_SIZE,
  REPORT_PARAMETERS,
  REPORT_TYPES,
  STATUS_SELECTOR,
} from '../../shared/utils/constants';
import Filter from './components/Filter';
import columns from './components/Columns';
import { contractListByContractFilter } from '../../shared/queries/Contract';
import { generateContract, openReport } from '../../shared/utils/smartutils';
import { responsibleListAllQuery } from '../../shared/queries/Responsible';
import { studentListByFilter } from '../../shared/queries/Student';
import { contractList } from '../../shared/queries/AccountsReceivableAndPayable';

const Contract = (props) => {
  const { t, history, dispatch } = props;

  const [data, setData] = useState([]);
  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const [lastFilter, setLastFilter] = useState(null);
  const [responsible, setResponsible] = useState([]);
  const [students, setStudents] = useState([]);
  const [contracts, setContracts] = useState([]);

  const showLoading = () => {
    dispatch(changeLoadingTrue());
  };

  const hideLoading = () => {
    dispatch(changeLoadingFalse());
  };

  const onFilterToggle = () => {
    setFilterIsOpen(!filterIsOpen);
  };

  const addItemClickhandler = () => {
    history.push(`/minime/contrato/mnt/0`);
  };

  const editItemClickHandler = (evt, rowData) => {
    history.push(`/minime/contrato/mnt/${rowData.id}`);
  };

  const listAllStudents = () => {
    const filter = {
      id: 0,
      classes: [],
      responsibles: [],
      menus: [],
      onlyActive: true,
    };

    const query = studentListByFilter(filter);
    GraphQl(query, {}).then((res) => {
      res.studentListByFilter = res.studentListByFilter.map((item) => ({
        ...item,
        label: item.fullName,
        value: item.id,
      }));
      setStudents(res.studentListByFilter);
    });
  };

  const listAllResponsible = () => {
    const query = responsibleListAllQuery();
    GraphQl(query, {}).then((res) => {
      res.responsibleListAll = res.responsibleListAll.map((item) => ({
        ...item,
        label: item.fullName,
        value: item.id,
      }));
      setResponsible(res.responsibleListAll);
    });
  };

  const loadAllContracts = () => {
    const query = contractList();
    GraphQl(query, {}).then((res) => {
      res.contractListByFilter = res.contractListByFilter.map((item) => ({
        id: item.id,
        value: item.id,
        label: item.numberContract,
      }));
      setContracts(res.contractListByFilter);
    });
  };

  const loadData = (filter) => {
    if (!filter.initialDate || !filter.finalDate) {
      showNotificationAutoDestroy(
        'warning',
        t('notifications.attention'),
        t('notifications.warning-no-date-selected')
      );
    } else {
      showLoading();
      if (filterIsOpen) {
        onFilterToggle();
      }
      const query = contractListByContractFilter(filter);
      GraphQl(query, {})
        .then((res) => {
          res.contractListByContractFilter =
            res.contractListByContractFilter.map((contract) => ({
              ...contract,
              initialDateFormatted: format(
                new Date(contract.initialDate),
                'dd/MM/yyyy'
              ),
              finalDateFormatted: format(
                new Date(contract.finalDate),
                'dd/MM/yyyy'
              ),
              validityTerm: differenceInMonths(
                contract.finalValidityDate
                  ? contract.finalValidityDate
                  : contract.finalDate,
                new Date(contract.initialDate).getTime()
              ),
              statusFormatted: t(`status.${contract.status}`),
            }));
          setData(res.contractListByContractFilter);
          hideLoading();
        })
        .catch(() => {
          hideLoading();
          showNotificationAutoDestroy(
            'danger',
            t('errors.title'),
            t('errors.error-to-loading')
          );
        });
    }
  };

  const onFilterClickHandler = (filter) => {
    if (filter) {
      setLastFilter(filter);
      loadData(filter);
    } else if (lastFilter) {
      loadData(lastFilter);
    } else {
      loadData({
        id: 0,
        initialDate: startOfMonth(new Date()).getTime(),
        finalDate: lastDayOfMonth(new Date()).getTime(),
        status: STATUS_SELECTOR.ACTIVE,
      });
    }
  };

  const filterBuildFunction = () => (
    <Filter
      filterToggle={onFilterToggle}
      isFiltering={filterIsOpen}
      filterClick={onFilterClickHandler}
      lastFilter={lastFilter}
      responsible={responsible}
      students={students}
      contracts={contracts}
    />
  );

  const printRegistrationForm = async (e, rowData) => {
    const formData = new FormData();

    formData.append(REPORT_PARAMETERS.REPORTTYPE, REPORT_TYPES.RELA_CONTRA);
    formData.append(REPORT_PARAMETERS.KEY, rowData.contractKey);

    showLoading();
    generateContract(formData)
      .then(async (res) => {
        openReport(res.data);
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('report.error-generate')
        );
      });
  };

  useEffect(() => {
    onFilterClickHandler();
    listAllStudents();
    listAllResponsible();
    loadAllContracts();
  }, []);

  return (
    <Fragment>
      <Container>
        <Row className="Table">
          <Col md={12} lg={12}>
            <Card>
              <CardBody className="material-table-card">
                <div className="material-table__wrap">
                  <MaterialTable
                    data={data}
                    showFilter
                    isFiltering={filterIsOpen}
                    filterToggle={onFilterToggle}
                    filterBuildFunction={filterBuildFunction}
                    columns={columns(t)}
                    pageSize={DEFAULT_PAGE_SIZE}
                    onAddClick={addItemClickhandler}
                    onEditClick={editItemClickHandler}
                    title={t('screens.mnt_contratos.title')}
                    paging
                    onPrintClick={printRegistrationForm}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

Contract.propTypes = {
  t: PropTypes.func.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default withTranslation('common')(
  connect((state) => ({
    loading: state.loading,
  }))(Contract)
);
